import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightActiveUserRmMap,
  storeFreightActiveUserRmMap,
  getFreightNewUserRmMap,
  storeFreightNewUserRmMap
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import { formatDateTimeDdMmYyyyAmPm } from "../../../utils/helpers";
import "./adminFreight.css";

const formatRmDateTime = (value) => {
  const formatted = formatDateTimeDdMmYyyyAmPm(value);
  return formatted || "—";
};

/** RM map GET may return rows on `data` or nested `data.data`; history as `rmList` or `rm_list`. */
function extractFreightRmMapPayload(res) {
  const top = res?.data;
  if (top != null && typeof top === "object" && !Array.isArray(top)) {
    if (Array.isArray(top.employeeList) || Array.isArray(top.rmList) || Array.isArray(top.rm_list)) {
      return top;
    }
    const inner = top.data;
    if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
      if (
        Array.isArray(inner.employeeList) ||
        Array.isArray(inner.rmList) ||
        Array.isArray(inner.rm_list)
      ) {
        return inner;
      }
    }
  }
  if (res != null && typeof res === "object" && !Array.isArray(res)) {
    if (Array.isArray(res.employeeList) || Array.isArray(res.rmList) || Array.isArray(res.rm_list)) {
      return res;
    }
  }
  return {};
}

const FreightRmMapPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = location.pathname.includes("/new_users/");

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = [
    { label: roleLabel, link: "/dashboard" },
    { label: "Freight", link: "/freight" },
    { label: `RM map ${id}` }
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [rmId, setRmId] = useState("");
  const [rmHistory, setRmHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = isNew ? await getFreightNewUserRmMap(id) : await getFreightActiveUserRmMap(id);
        const payload = extractFreightRmMapPayload(res);
        const list = payload.employeeList ?? [];
        setEmployees(list);
        const history = payload.rmList ?? payload.rm_list;
        setRmHistory(Array.isArray(history) ? history : []);
        const selected = list.find((e) => e.isSelected);
        setRmId(selected ? String(selected.id) : "");
      } catch (e) {
        showError(e.message || "Failed to load RM map");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await storeFreightNewUserRmMap(id, { rm_id: rmId || "" });
      } else {
        await storeFreightActiveUserRmMap(id, { rm_id: rmId || "" });
      }
      showSuccess("RM updated");
      navigate(isNew ? "/freight/new_users" : "/freight/active_users");
    } catch (e) {
      showError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
        <p>Loading…</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[960px]">
        <div className="card offers-table">
          <div className="flex justify-between items-center mb-2">
            <h3>Relationship manager</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Select one admin as RM (or leave unassigned).
          </p>

          <div
            className="freight-rm-list"
            style={{ maxWidth: "520px", border: "1px solid #e8ecf2", borderRadius: "8px" }}
          >
            <label style={{ fontWeight: 600 }}>
              <input
                type="radio"
                name="rm"
                checked={rmId === ""}
                onChange={() => setRmId("")}
              />
              <span>No RM</span>
            </label>
            {employees.map((emp) => (
              <label key={emp.id}>
                <input
                  type="radio"
                  name="rm"
                  checked={rmId === String(emp.id)}
                  onChange={() => setRmId(String(emp.id))}
                />
                <span>
                  {emp.name || emp.email || emp.mobile_no || `Admin #${emp.id}`}
                </span>
              </label>
            ))}
          </div>

          <div className="form-actions" style={{ marginTop: "20px" }}>
            <button type="button" className="btn-primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-outline" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>

          {rmHistory.length > 0 ? (
            <div style={{ marginTop: "28px" }}>
              <h4
                className="freight-form-section-title"
                style={{ marginBottom: "12px", borderTop: "1px solid #e8ecf2", paddingTop: "20px" }}
              >
                RM assignment history
              </h4>
              <div className="freight-admin-table-wrap">
                <table className="freight-admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>RM name</th>
                      <th>Start date/time</th>
                      <th>End date/time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rmHistory.map((row, index) => {
                      const endRaw = row.unassign_date;
                      const hasEnd = endRaw != null && String(endRaw).trim() !== "";
                      return (
                        <tr key={row.id ?? `${row.rm_id}-${row.assign_date}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{row.rm_name || "—"}</td>
                          <td>{formatRmDateTime(row.assign_date)}</td>
                          <td>{hasEnd ? formatRmDateTime(endRaw) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightRmMapPage;
